import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Paper,
  Typography,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Box,
  Divider,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import "./AddNewBlogs.scss";
import BasicInformationFields from "../../../../library/common/BasicInformationFields";
import {
  FileDownload as ExportIcon,
  FileUpload as ImportIcon
} from "@mui/icons-material"
import { api } from "../../../../services";
import MultiInput from "../../../../library/common/MultiInput";
import { CONSTANTS } from "../../../../library/common/constant";
import { useNavigate, useParams } from "react-router-dom";
import MetaFields from "../../../../library/common/MetaFields";
import { debounce } from "lodash";
import FileUploadPreview from "../../../../library/common/FileUploadPreview";
import AuthorFields from "../../../../library/common/AuthorFields";
import SocialMediaField from "../../../../library/common/SocialMediaField";
import { throttle } from "lodash";
import CustomDialog from "../../../../library/common/CustomDialog";
import { exportToJson, importJsonDraft } from "../../../../library/common/exportImportDraft";

const cleanDraftData = (obj) => {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof File) {
    return {
      name: obj.name,
      size: obj.size,
      type: obj.type,
      isDraftPlaceholder: true,
    };
  }
  if (Array.isArray(obj)) {
    return obj
      .map(item => cleanDraftData(item))
      .filter(item => item !== undefined);
  }
  if (typeof obj === "object") {
    const cleaned = {};
    for (const [key, value] of Object.entries(obj)) {
      const cleanedVal = cleanDraftData(value);
      if (cleanedVal !== undefined) {
        cleaned[key] = cleanedVal;
      }
    }
    return cleaned;
  }
  return obj;
};

const useFileUpload = (setUpdatedData, setErrors) => {
  const handleFileChange = useCallback(
    (e) => {
      const { name, files, multiple } = e.target;
      const maxFiles = 10;

      if (files?.length > 0) {
        const fileArray = [...files];
        const maxSize = 10 * 1024 * 1024;

        const validFiles = fileArray.filter((file) => file.size <= maxSize);
        const invalidFiles = fileArray.filter((file) => file.size > maxSize);

        if (invalidFiles.length > 0 && setErrors) {
          setErrors((prev) => ({
            ...prev,
            [name]: `File(s) ${invalidFiles
              .map((f) => f.name)
              .join(", ")} are too large (max 10MB).`,
          }));
        } else if (setErrors) {
          setErrors((prev) => ({ ...prev, [name]: null }));
        }

        if (validFiles.length === 0) return;
        setUpdatedData((prev) => {
          if (multiple) {
            const existingFiles = prev[name] || [];

            const availableSlots = maxFiles - existingFiles?.length || 0;

            if (availableSlots <= 0) {
              if (setErrors) {
                setErrors((e) => ({
                  ...e,
                  [name]: `You can only upload a maximum of ${maxFiles} items.`,
                }));
              }
              return prev;
            }

            const filesToAdd = validFiles.slice(0, availableSlots);

            if (validFiles.length > availableSlots) {
              if (setErrors) {
                setErrors((e) => ({
                  ...e,
                  [name]: `You can only add ${availableSlots} more items. ${validFiles.length - availableSlots
                    } files were not added.`,
                }));
              }
            }
            return {
              ...prev,
              [name]: [...existingFiles, ...filesToAdd],
            };
          } else {
            return {
              ...prev,
              [name]: validFiles[0],
            };
          }
        });
      } else if (!multiple) {
        setUpdatedData((prev) => ({ ...prev, [name]: null }));
      }
    },
    [setUpdatedData, setErrors]
  );

  return { handleFileChange };
};

const {
  ADD_BLOGS_TITLE,
  ADD_BLOGS_DESCRIPTION,
  COVER_DETAILS,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
  PUBLISHING_DETAILS,
  SOCIAL_LINKS,
  META_DETAILS,
  STATUS_OPTIONS,
  UPDATE_BUTTON,
  CREATE_BUTTON,
  THROTTLE_DURATION,
  UPDATE_BLOGS_TITLE,
  UPDATE_BLOGS_DESCRIPTION,
} = CONSTANTS;

const AddNewBlogs = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const isEditMode = Boolean(slug);
  const [formData, setFormData] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEditMode);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [updatedData, setUpdatedData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const formRef = useRef(null);
  const importInputRef = useRef(null);

  // Draft and Toast states
  const [toast, setToast] = useState({ open: false, message: "", severity: "error" });
  const [draftDialogOpen, setDraftDialogOpen] = useState(false);
  const [pendingDraft, setPendingDraft] = useState(null);

  const { handleFileChange } = useFileUpload(setUpdatedData, setErrors);

  const getFieldValue = useCallback(
    (fieldPath) => {
      const keys = fieldPath.split(".");
      let value = updatedData[fieldPath];
      if (value === undefined) {
        value = keys.reduce(
          (acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined),
          formData
        );
      }
      return value;
    },
    [updatedData, formData]
  );

  const validateSlug = useCallback(
    async (slugVal) => {
      if (updatedData?.slug === formData?.slug) return true;
      try {
        const { data } = await api.get(`slug/blog/${slugVal}`);
        if (!data)
          setErrors((prev) => ({ ...prev, slug: "Slug is not unique" }));
        return data;
      } catch {
        setErrors((prev) => ({ ...prev, slug: "Slug validation failed" }));
        return false;
      }
    },
    [updatedData, formData]
  );

  const debouncedValidateSlug = useMemo(
    () =>
      debounce(async (slugVal) => {
        await validateSlug(slugVal);
      }, 500),
    [validateSlug]
  );

  useEffect(() => {
    return () => {
      debouncedValidateSlug.cancel();
    };
  }, [debouncedValidateSlug]);

  const focusFirstError = (newErrors) => {
    const firstErrorKey = Object.keys(newErrors)[0];
    if (!firstErrorKey) return;

    let element = document.getElementsByName(firstErrorKey)[0];
    if (!element) {
      element = document.getElementById(firstErrorKey);
    }
    if (!element) {
      element = document.querySelector(`[name="${firstErrorKey}"]`);
    }
    if (!element) {
      element = document.querySelector(
        `input[name*="${firstErrorKey}"], select[name*="${firstErrorKey}"], textarea[name*="${firstErrorKey}"]`
      );
    }

    if (element) {
      element.focus();
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const handleApiError = (error) => {
    const data = error.response?.data;
    const message = data?.message || error.message || "An error occurred.";
    const errorsPayload = data?.errors;

    let displayMessage = message;
    if (errorsPayload) {
      if (typeof errorsPayload === "string") {
        displayMessage = errorsPayload;
      } else if (typeof errorsPayload === "object") {
        const messages = [];
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            messages.push(value.message || JSON.stringify(value));
          } else {
            messages.push(String(value));
          }
        }
        displayMessage = messages.join(", ");
      }
    }

    const isSizeError = /size|large|limit|upload/i.test(displayMessage) || /size|large|limit|upload/i.test(message);
    if (isSizeError) {
      const isImage = /image|cover|authorImage|featured/i.test(displayMessage) || /image|cover|authorImage|featured/i.test(message) || /image|cover|authorImage|featured/i.test(JSON.stringify(errorsPayload));
      const isFile = /file|pdf/i.test(displayMessage) || /file|pdf/i.test(message) || /file|pdf/i.test(JSON.stringify(errorsPayload));
      if (isImage) {
        displayMessage = "Use URL image or image size is large";
      } else if (isFile) {
        displayMessage = "Use URL file or file size is large";
      } else {
        displayMessage = "Use URL image or file size is large";
      }
    }

    setToast({
      open: true,
      message: displayMessage,
      severity: "error",
    });

    if (errorsPayload) {
      const parsedErrors = {};
      if (typeof errorsPayload === "string") {
        const parts = errorsPayload.split(",");
        parts.forEach((part) => {
          const match = part.match(/"([^"]+)"\s+(.+)/);
          if (match) {
            const field = match[1];
            const desc = match[2];
            parsedErrors[field] = `Server: "${field}" ${desc}`;
          }
        });
      } else if (typeof errorsPayload === "object") {
        for (const [key, value] of Object.entries(errorsPayload)) {
          if (value && typeof value === "object") {
            parsedErrors[key] = value.message || String(value);
          } else {
            parsedErrors[key] = String(value);
          }
        }
      }
      if (Object.keys(parsedErrors).length > 0) {
        setErrors((prev) => ({ ...prev, ...parsedErrors }));
        focusFirstError(parsedErrors);
      }
    } else if (message.includes("Duplicate Key Error") || error.response?.status === 409) {
      if (message.includes("slug")) {
        setErrors((prev) => ({ ...prev, slug: "Slug already exists." }));
        focusFirstError({ slug: true });
      }
    }
  };


  // Export current page blogs as JSON
  const handleExport = () => {
    const ok = exportToJson(updatedData, "blogs");
    setToast({
      open: true,
      message: ok ? "Blogs exported successfully!" : "Export failed.",
      severity: ok ? "success" : "error",
    });
  };

  // Import JSON draft
  const handleImportClick = () => importInputRef.current?.click();
  const handleImportFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = null;
    importJsonDraft(
      file,
      "draft_blog",
      navigate,
      "/blog/add",
      (msg) => setToast({ open: true, message: msg, severity: "success" }),
      (msg) => setToast({ open: true, message: msg, severity: "error" })
    );
  };

  const isValidObjectId = (id) =>
    typeof id === "string" && /^[a-f\d]{24}$/i.test(id);
  const isValidSlug = (s) =>
    typeof s === "string" && /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/.test(s);
  const isValidUrl = (url) => {
    try {
      if (!url) return true;
      if (/^data:image\//.test(url)) return false;
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };
  const isValidDate = (date) => !date || !isNaN(Date.parse(date));
  const isValidStatus = (status) =>
    ["draft", "published", "archived"].includes(status);

  const validateForm = () => {
    const newErrors = {};
    const getVal = (field) => {
      const val = getFieldValue(field);
      return typeof val === "string" ? val.trim() : val;
    };
    // Slug
    const slugVal = getVal("slug");
    if (!slugVal) newErrors["slug"] = "Slug is required";
    else if (!isValidSlug(slugVal))
      newErrors["slug"] = "Invalid slug format (lowercase, hyphens allowed)";

    // Title
    if (!getVal("title.en"))
      newErrors["title.en"] = "English title is required";
    if (!getVal("title.hi")) newErrors["title.hi"] = "Hindi title is required";

    // Short Description
    if (!getVal("shortDescription.en"))
      newErrors["shortDescription.en"] = "Short description (EN) is required";
    if (!getVal("shortDescription.hi"))
      newErrors["shortDescription.hi"] = "Short description (HI) is required";

    // Blog Text
    if (!getVal("longDescription.en"))
      newErrors["longDescription.en"] = "Content (EN) is required";
    if (!getVal("longDescription.hi"))
      newErrors["longDescription.hi"] = "Content (HI) is required";

    // Author (name.en, name.hi)
    if (!getVal("author.name.en"))
      newErrors["author.name.en"] = "Author name (EN) is required";
    if (!getVal("author.name.hi"))
      newErrors["author.name.hi"] = "Author name (HI) is required";

    // Cover Image URL (optional, must be valid URL if present)
    const coverImage = updatedData["coverImage"];
    const coverImageUrl = getVal("coverImageUrl");
    if (coverImage && coverImage.isDraftPlaceholder) {
      newErrors["coverImageUrl"] = "Please re-upload the cover image (draft files must be re-selected)";
    } else if (coverImageUrl && !isValidUrl(coverImageUrl)) {
      newErrors["coverImageUrl"] = "Cover image URL is invalid";
    }

    // Cover Video URL (optional, must be valid URL if present)
    const coverVideo = updatedData["coverVideo"];
    const coverVideoUrl = getVal("coverVideoUrl");
    if (coverVideo && coverVideo.isDraftPlaceholder) {
      newErrors["coverVideoUrl"] = "Please re-upload the cover video (draft files must be re-selected)";
    } else if (coverVideoUrl && !isValidUrl(coverVideoUrl)) {
      newErrors["coverVideoUrl"] = "Cover video URL is invalid";
    }

    // Author Image URL (optional)
    const authorImg = updatedData["authorImage"];
    const authorImgUrl = getVal("author.image");
    if (authorImg && authorImg.isDraftPlaceholder) {
      newErrors["author.image"] = "Please re-upload the author image (draft files must be re-selected)";
    } else if (authorImgUrl && !isValidUrl(authorImgUrl)) {
      newErrors["author.image"] = "Author image URL is invalid";
    }

    // Gallery Draft Placeholder Check
    const gallery = getFieldValue("gallery") || [];
    if (gallery.some((item) => item && item.isDraftPlaceholder)) {
      newErrors["gallery"] = "Please re-upload draft files in gallery (draft files must be re-selected)";
    }

    // Categories
    const cats = getFieldValue("categories");
    if (!Array.isArray(cats) || cats.length === 0)
      newErrors["categories"] = "At least one category is required";
    else if (!cats.every(isValidObjectId))
      newErrors["categories"] = "Invalid category ID(s)";

    // Part Number
    const partEn = getVal("part.en");
    if (partEn && (!Number.isInteger(Number(partEn)) || Number(partEn) < 1))
      newErrors["part.en"] = "Part number (EN) must be integer >= 1";
    const partHi = getVal("part.hi");
    if (partHi && (!Number.isInteger(Number(partHi)) || Number(partHi) < 1))
      newErrors["part.hi"] = "Part number (HI) must be integer >= 1";

    // Linked IDs
    const linkedBlogsIds = getVal("linkedBlogsIds");
    if (
      linkedBlogsIds &&
      Array.isArray(linkedBlogsIds) &&
      !linkedBlogsIds.every(isValidObjectId)
    )
      newErrors["linkedBlogsIds"] = "Invalid linked blog ID(s)";

    // Blog Series ID
    const blogSeriesId = getVal("blogSeriesId");
    if (blogSeriesId && !isValidObjectId(blogSeriesId))
      newErrors["blogSeriesId"] = "Invalid blog series ID";

    // Expiry and Publish Dates
    if (getVal("tillDate") && !isValidDate(getVal("tillDate")))
      newErrors["tillDate"] = "Invalid expiry date";
    if (getVal("publishDate") && !isValidDate(getVal("publishDate")))
      newErrors["publishDate"] = "Invalid publish date";

    // Status
    const status = getVal("status");
    if (status && !isValidStatus(status))
      newErrors["status"] = "Invalid status";

    // Social Media Links (optional)
    const socialLinks = getVal("socialMediaLinks");
    if (socialLinks && typeof socialLinks === "object") {
      ["instagram", "twitter", "facebook", "linkden"].forEach((key) => {
        if (socialLinks[key] && !isValidUrl(socialLinks[key])) {
          newErrors[`socialMediaLinks.${key}`] = `Invalid ${key} URL`;
        }
      });
      if (
        socialLinks.ortherLinks &&
        typeof socialLinks.ortherLinks === "object"
      ) {
        Object.entries(socialLinks.ortherLinks).forEach(([k, v]) => {
          if (v && !isValidUrl(v))
            newErrors[
              `socialMediaLinks.ortherLinks.${k}`
            ] = `Invalid other link URL (${k})`;
        });
      }
    }

    // Meta Fields Canonical and Image URLs
    const metafieldsCanonical = getVal("metafields.canonical");
    if (metafieldsCanonical && !isValidUrl(metafieldsCanonical)) {
      newErrors["metafields.canonical"] = "Canonical URL is invalid";
    }
    const metafieldsImage = getVal("metafields.image");
    if (metafieldsImage && !isValidUrl(metafieldsImage)) {
      newErrors["metafields.image"] = "Meta image URL is invalid";
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      focusFirstError(newErrors);
      setToast({ open: true, message: "Please correct the errors in the form.", severity: "error" });
    }
    return Object.keys(newErrors).length === 0;
  };

  const handleRemoveGalleryItem = (indexToRemove) => {
    const currentGallery = getFieldValue("gallery") || [];
    const currentGalleryUrls = getFieldValue("galleryUrls") || [];

    if (indexToRemove < currentGallery.length) {
      const updatedGallery = currentGallery.filter(
        (_, index) => index !== indexToRemove
      );
      setUpdatedData((prev) => ({
        ...prev,
        gallery: updatedGallery,
      }));
    } else {
      const realIndex = indexToRemove - currentGallery.length;
      const updatedGalleryUrls = currentGalleryUrls.filter(
        (_, index) => index !== realIndex
      );
      setUpdatedData((prev) => ({
        ...prev,
        galleryUrls: updatedGalleryUrls,
      }));
    }
  };

  const handleAddGalleryUrl = (url) => {
    if (!url) return;
    const maxFiles = 10;

    setUpdatedData((prev) => {
      const existingItems = prev.gallery
        ? Array.isArray(prev.gallery)
          ? prev.gallery
          : [prev.gallery]
        : [];

      if (existingItems.length >= maxFiles) {
        if (setErrors) {
          setErrors((e) => ({
            ...e,
            gallery: `You can only have a maximum of ${maxFiles} items.`,
          }));
        }
        return prev;
      }

      return {
        ...prev,
        gallery: [...existingItems, url],
      };
    });
  };

  const handleChange = useCallback(
    (e) => {
      let { name, value, type, checked } = e.target;
      if (name === "slug" && typeof value === "string") {
        value = value.replace(/\s+/g, "-");
      }
      const newValue = type === "checkbox" ? checked : value;
      setUpdatedData((prev) => ({ ...prev, [name]: newValue }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
      if (name === "slug") {
        debouncedValidateSlug(newValue);
      }
    },
    [errors, debouncedValidateSlug]
  );

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    const cleanPayload = (obj) => {
      if (obj === null || obj === undefined) return undefined;
      if (obj instanceof File) return obj;
      if (Array.isArray(obj)) {
        const cleaned = obj.map(cleanPayload).filter(v => v !== undefined);
        return cleaned.length > 0 ? cleaned : undefined;
      }
      if (typeof obj === "object") {
        const cleaned = {};
        let hasKeys = false;
        for (const [k, v] of Object.entries(obj)) {
          if (v === "" || v === null || v === undefined) {
            continue;
          }
          const cv = cleanPayload(v);
          if (cv !== undefined) {
            cleaned[k] = cv;
            hasKeys = true;
          }
        }
        return hasKeys ? cleaned : undefined;
      }
      return obj;
    };

    try {
      const cleanedData = cleanPayload(updatedData) || {};
      const blogData = new FormData();

      // Handle gallery files
      const finalGallery = getFieldValue("gallery") || [];
      finalGallery.forEach((item) => {
        if (item instanceof File) {
          blogData.append("gallery", item);
        } else if (typeof item === "string" && !item.isDraftPlaceholder) {
          blogData.append("galleryUrls", item);
        }
      });

      const finalGalleryUrls = getFieldValue("galleryUrls") || [];
      finalGalleryUrls.forEach((item) => {
        if (typeof item === "string") {
          blogData.append("galleryUrls", item);
        }
      });

      // Handle other fields from cleanedData
      for (const key in cleanedData) {
        if (key === "gallery") continue;
        if (key === "galleryUrls") continue;
        if (key === "coverImage") continue;
        if (key === "coverVideo") continue;
        if (key === "authorImage") continue;
        if (cleanedData[key] && cleanedData[key].isDraftPlaceholder) continue;

        let value = cleanedData[key];
        if (key === "slug" && typeof value === "string") {
          value = value.replace(/\s+/g, "-").replace(/^-+|-+$/g, "");
        }

        if (
          typeof value === "object" &&
          !Array.isArray(value) &&
          !(value instanceof File)
        ) {
          blogData.append(key, JSON.stringify(value));
        } else if (Array.isArray(value)) {
          blogData.append(key, value.join(","));
        } else {
          blogData.append(key, value);
        }
      }

      // Explicitly append cover/author image file uploads if present
      const coverImage = updatedData["coverImage"];
      if (coverImage instanceof File) {
        blogData.append("coverImage", coverImage);
      }
      const coverVideo = updatedData["coverVideo"];
      if (coverVideo instanceof File) {
        blogData.append("coverVideo", coverVideo);
      }
      const authorImage = updatedData["authorImage"];
      if (authorImage instanceof File) {
        blogData.append("authorImage", authorImage);
      }

      if (isEditMode) {
        blogData.append("verified", true);
      }

      formRef.current = blogData;

      // API call
      if (isEditMode) {
        handleEdit();
      } else {
        handleAdd();
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setToast({
        open: true,
        message: "An error occurred while saving the blog. Please try again.",
        severity: "error",
      });
    }
  };

  const returnBack = () => {
    navigate("/blog/list", { replace: true });
  };

  const handleAdd = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.post("/blogs", form, { skipAlert: true });
      localStorage.removeItem("draft_blog");
      returnBack();
    } catch (error) {
      console.error("Failed to post: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  const handleEdit = throttle(async () => {
    try {
      setIsLoading(true);
      const form = formRef.current;
      await api.patch(`/blogs/${formData?._id}`, form, { skipAlert: true });
      returnBack();
    } catch (error) {
      console.error("Failed to save: ", error);
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  }, THROTTLE_DURATION);

  // Autosave draft effect
  useEffect(() => {
    if (isEditMode || Object.keys(updatedData).length === 0) return;

    const save = () => {
      try {
        const cleaned = cleanDraftData(updatedData);
        localStorage.setItem("draft_blog", JSON.stringify({
          data: cleaned,
          timestamp: Date.now(),
        }));
      } catch (err) {
        console.error("Failed to save draft:", err);
      }
    };

    const timeout = setTimeout(save, 1000);
    return () => clearTimeout(timeout);
  }, [updatedData, isEditMode]);

  // Load draft check on mount
  useEffect(() => {
    if (isEditMode) return;
    try {
      const stored = localStorage.getItem("draft_blog");
      if (stored) {
        const { data, timestamp } = JSON.parse(stored);
        const THIRTY_DAYS = 30 * 24 * 60 * 60 * 1000;
        if (Date.now() - timestamp < THIRTY_DAYS) {
          setPendingDraft(data);
          setDraftDialogOpen(true);
        } else {
          localStorage.removeItem("draft_blog");
        }
      }
    } catch (err) {
      console.error("Failed to check draft:", err);
    }
  }, [isEditMode]);

  const handleRestoreDraft = () => {
    if (pendingDraft) {
      setUpdatedData(pendingDraft);
      setToast({ open: true, message: "Draft restored successfully!", severity: "success" });
    }
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleDeleteDraft = () => {
    localStorage.removeItem("draft_blog");
    setToast({ open: true, message: "Draft deleted.", severity: "info" });
    setDraftDialogOpen(false);
    setPendingDraft(null);
  };

  const handleResetForm = () => {
    setUpdatedData({});
    localStorage.removeItem("draft_blog");
    setErrors({});
    setToast({ open: true, message: "Form reset and draft cleared.", severity: "info" });
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await api.get("/categories");
        const blogCategories = data?.categories?.filter(
          (category) => category?.isBlog
        );
        setCategories(blogCategories);
      } catch (error) {
        setApiError("Failed to load categories.");
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    const fetchBlogDetails = async () => {
      setLoading(true);
      setApiError("");
      try {
        const { data } = await api.get(`/blogs/${slug}`);
        if (data) {
          data.coverImageUrl = data.coverImage;
          data.coverVideoUrl = data.coverVideo;
          data.galleryUrls = data.gallery;
          data.gallery = [];
          delete data.coverImage;
          delete data.coverVideo;
          data.categories = data?.categories?.map((cat) => cat._id);
          setFormData(data);
        }
      } catch (error) {
        console.error("Error fetching blog details:", error);
        setApiError("Failed to load blog details.");
      } finally {
        setLoading(false);
      }
    };
    fetchBlogDetails();
  }, [isEditMode, slug]);

  const getDate = (date) => {
    if (date) return new Date(date).toISOString().split("T")[0];
    return new Date().toISOString().split("T")[0];
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper className="paperContainer">
      <Typography variant="h5" gutterBottom>
        {isEditMode ? UPDATE_BLOGS_TITLE : ADD_BLOGS_TITLE}
      </Typography>
      <Typography variant="body2" gutterBottom>
        {isEditMode ? UPDATE_BLOGS_DESCRIPTION : ADD_BLOGS_DESCRIPTION}
      </Typography>

      {!isEditMode ? (<Box display="flex" justifyContent="flex-end" gap={2}>
        {/* Hidden import file input */}
        <input
          type="file"
          accept="application/json"
          ref={importInputRef}
          style={{ display: "none" }}
          onChange={handleImportFile}
        />
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ExportIcon />}
          onClick={handleExport}
          title="Export current page as JSON"
        >
          Export
        </Button>
        <Button
          variant="outlined"
          size="medium"
          startIcon={<ImportIcon />}
          onClick={handleImportClick}
          title="Import JSON to add form"
        >
          Import
        </Button>
      </Box>) : null}

      <form onSubmit={handleSubmit}>
        <BasicInformationFields
          updatedData={updatedData}
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          getFieldValue={getFieldValue}
          page="blog"
        />

        <Divider variant="middle" />
        <AuthorFields
          getFieldValue={getFieldValue}
          handleChange={handleChange}
          handleFileChange={handleFileChange}
          error={apiError}
          errors={errors}
        />
        <Divider variant="middle" />
        <Box mb={3} mt={3}>
          <Typography variant="h6" gutterBottom>
            {COVER_DETAILS}
          </Typography>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <FileUploadPreview
              label="Cover Image / कवर इमेज"
              id="cover-image-upload"
              fileField="coverImage"
              acceptedTypes={ACCEPTED_IMAGE_TYPES.join(",")}
              allowUrl
              urlField="coverImageUrl"
              getFieldValue={getFieldValue}
              onFileChange={handleFileChange}
              onUrlChange={handleChange}
              error={errors["coverImageUrl"]}
              errors={errors}
            />
          </Box>
        </Box>
        {/* Cover Video media */}
        <Box mb={3} mt={3}>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <FileUploadPreview
              label="Cover Video / कवर वीडियो"
              id="cover-video-upload"
              fileField="coverVideo"
              acceptedTypes={ACCEPTED_VIDEO_TYPES.join(",")}
              allowUrl
              urlField="coverVideoUrl"
              getFieldValue={getFieldValue}
              onFileChange={handleFileChange}
              onUrlChange={handleChange}
              error={errors["coverVideoUrl"]}
              errors={errors}
            />
          </Box>
        </Box>
        <Divider variant="middle" />

        <Box mb={3} mt={3}>
          <Box sx={{ flex: 1, minWidth: "300px" }}>
            <FileUploadPreview
              label="Gallery Images / गैलरी इमेज अपलोड करें"
              id="gallery-image-upload"
              fileField="gallery"
              acceptedTypes={ACCEPTED_IMAGE_TYPES.join(",")}
              multiple
              allowUrl
              urlField="galleryUrls"
              getFieldValue={getFieldValue}
              onFileChange={handleFileChange}
              onUrlChange={handleChange}
              onRemoveItem={handleRemoveGalleryItem}
              onAddUrl={handleAddGalleryUrl}
              error={errors["gallery"]}
              errors={errors}
            />
          </Box>
        </Box>
        <Divider variant="middle" />

        <Box mb={3} mt={3}>
          <Typography variant="h6" gutterBottom>
            {PUBLISHING_DETAILS}
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              fullWidth
              label="Publish Date"
              name="publishDate"
              type="date"
              value={getDate(getFieldValue("publishDate")) || ""}
              onChange={handleChange}
            />

            <TextField
              fullWidth
              label="Expiry Date"
              name="tillDate"
              type="date"
              value={getDate(getFieldValue("tillDate")) || ""}
              onChange={handleChange}
            />
          </Stack>
        </Box>
        <Divider variant="middle" />
        <Box mb={3} mt={3}>
          <Typography variant="h6" gutterBottom>
            {SOCIAL_LINKS}
          </Typography>
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
            <Stack direction="row" gap={2} width="100%">
              <TextField
                fullWidth
                label="Part Number"
                type="number"
                value={getFieldValue("part.en") || ""}
                onChange={handleChange}
                name="part.en"
                error={!!errors["part.en"]}
                helperText={errors["part.en"]}
              />
              <TextField
                fullWidth
                label="Part Number Hindi"
                type="text"
                value={getFieldValue("part.hi") || ""}
                onChange={handleChange}
                name="part.hi"
                error={!!errors["part.hi"]}
                helperText={errors["part.hi"]}
              />
            </Stack>
            <MultiInput
              initialValues={getFieldValue("category") || []}
              options={categories}
              onChange={handleChange}
              name="category"
              label="Linked Blog"
              type="object"
              select
            />
          </Box>
          <TextField
            fullWidth
            label="Remarks"
            name="remarks.en"
            value={getFieldValue("remarks.en") || ""}
            onChange={handleChange}
            multiline
            minRows={2}
            sx={{ my: 2 }}
            error={!!errors["remarks.en"]}
            helperText={errors["remarks.en"]}
          />
          <TextField
            fullWidth
            label="टिप्‍पणी"
            name="remarks.hi"
            value={getFieldValue("remarks.hi") || ""}
            onChange={handleChange}
            multiline
            minRows={2}
            error={!!errors["remarks.hi"]}
            helperText={errors["remarks.hi"]}
          />
        </Box>
        <Divider variant="middle" />
        {/* Social Media Links Section */}
        <SocialMediaField
          getFieldValue={getFieldValue}
          handleChange={handleChange}
          errors={errors}
        />
        <Divider variant="middle" />
        {/* Metadata Fields Section */}
        <Box className="section_container">
          <Typography variant="h6" gutterBottom>
            {META_DETAILS}
          </Typography>
          <MetaFields
            getFieldValue={getFieldValue}
            handleChange={handleChange}
            handleFileChange={handleFileChange}
            errors={errors}
          />
        </Box>
        <Divider />
        <Box mb={3} mt={3}>
          <Typography variant="h6" gutterBottom>
            Blog Status
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={getFieldValue("status") || ""}
              onChange={handleChange}
              label="Status"
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option?.value} value={option?.value}>
                  {option?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              name="isFeatured"
              checked={getFieldValue("isFeatured") || false}
              onChange={handleChange}
            />
          }
          label="Is Featured Blog"
        />

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          {!isEditMode && (
            <Button
              variant="outlined"
              color="secondary"
              size="large"
              onClick={handleResetForm}
              sx={{ flex: 1 }}
            >
              Reset Form
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={isLoading}
            sx={{ flex: !isEditMode ? 2 : 1 }}
          >
            {isEditMode ? UPDATE_BUTTON : CREATE_BUTTON}
          </Button>
        </Stack>
      </form>

      {/* Toast Notification */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToast((prev) => ({ ...prev, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Restore Draft Confirmation Dialog */}
      <CustomDialog
        open={draftDialogOpen}
        title="Restore Draft?"
        content="We found a saved draft for this form. Do you want to restore it?"
        primaryText="Restore"
        secondaryText="Delete Draft"
        onPrimaryClick={handleRestoreDraft}
        onSecondaryClick={handleDeleteDraft}
        onClose={() => setDraftDialogOpen(false)}
      />
    </Paper>
  );
};

export default AddNewBlogs;
